// The frame every screen renders inside. On the desk and the tablet a navy rail holds the
// destinations; on the phone it becomes a bottom bar. Which frame a device class gets is a
// setting. A role hides the destinations it cannot use. A login that cannot write is marked "Read only" beside its name,
// and the frame carries the `ro` class that draws every editing control as its value (ui/kit.tsx, styles.css).

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { insertRow, toast, useRows } from '../lib/api';
import { contactName } from '../lib/format';
import { ROLES, useSession } from '../lib/session';
import { Btn, Tag, usePopover, WRITE_CONTROLS } from '../ui/kit';
import { NewOpportunity } from '../screens/NewOpportunity';

export interface Crumb {
  label: string;
  to?: string;
}
const CrumbCtx = createContext<(c: Crumb[]) => void>(() => {});

/** A screen says where it is; the top bar shows it. */
export function useCrumb(parts: Crumb[]) {
  const set = useContext(CrumbCtx);
  const key = JSON.stringify(parts);
  useEffect(() => {
    set(parts);
    document.title = `${parts.map((p) => p.label).join(' › ')} · ZeroGap`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

interface Dest {
  key: string;
  label: string;
  to: string;
  any: string[];
  unless?: string;
  end?: boolean;
}

const DESTINATIONS: Dest[] = [
  { key: 'home', label: 'Home', to: '/', any: ['screen.home'], end: true },
  { key: 'jobs', label: 'Jobs', to: '/jobs', any: ['screen.jobs'] },
  { key: 'library', label: 'Library', to: '/library', any: ['screen.library'] },
  { key: 'shop', label: 'Shop', to: '/shop', any: ['screen.board_owner', 'screen.board_tablet'] },
  { key: 'scan', label: 'Scan', to: '/scan', any: ['screen.scan'] },
  { key: 'files', label: 'Files', to: '/files', any: ['screen.files'], unless: 'screen.jobs' },
  { key: 'inventory', label: 'Inventory', to: '/inventory', any: ['screen.purchasing', 'screen.receiving'] },
  { key: 'plan', label: 'Plan', to: '/plan', any: ['screen.landing_board'] },
  { key: 'people', label: 'Contacts', to: '/people', any: ['screen.contacts'] },
  { key: 'settings', label: 'Settings', to: '/settings', any: ['screen.settings', 'screen.change_log', 'screen.people'] },
];
const PHONE_ORDER = ['home', 'jobs', 'shop', 'scan', 'plan', 'files', 'inventory', 'library', 'people', 'settings'];

export function useDestinations(): Dest[] {
  const { can, setting } = useSession();
  // the Inventory destination's name is a setting, like the rest of that section's names
  const inventory = setting<{ rail?: string }>('inventory_names', {})?.rail;
  return useMemo(
    () => DESTINATIONS.filter((d) => d.any.some(can) && !(d.unless && can(d.unless))).map((d) => (d.key === 'inventory' && inventory ? { ...d, label: inventory } : d)),
    [can, inventory],
  );
}

function Mark() {
  return (
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="54.38 54.12 107.25 107.75">
      <path fill="currentColor" d="M113.09,117.34h23.56v19.22h-29.12c-15.49,0-28.1-12.6-28.1-28.1v-36.3h-7.65v36.3c0,19.71,16.04,35.75,35.75,35.75h36.77v-34.52h-31.21v7.65Z" />
      <rect fill="currentColor" x="92.93" y="72.04" width="31.14" height="7.65" />
      <rect fill="currentColor" x="102.36" y="90.33" width="31.14" height="7.65" />
      <path fill="currentColor" d="M54.48,54.19v107.63h107.05V54.19H54.48ZM62.4,153.89V62.11h91.2v91.78H62.4Z" />
    </svg>
  );
}

function Search() {
  const [text, setText] = useState('');
  const [armed, setArmed] = useState(false);
  const on = { enabled: armed };
  const opps = useRows('opportunity', on);
  const jobs = useRows('job', on);
  const items = useRows('job_item', on);
  const contacts = useRows('contact', on);
  const materials = useRows('material', on);

  const results = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (q.length < 2) return [];
    const out: { label: string; kind: string; to: string }[] = [];
    const hit = (s: unknown) => String(s ?? '').toLowerCase().includes(q);
    const oppName = new Map(opps.rows.map((o) => [o.id, o.name as string]));
    for (const j of jobs.rows) {
      const name = `${oppName.get(j.opportunity_id) ?? ''} · ${j.scope_name}`;
      if (hit(name)) out.push({ label: name, kind: 'Job', to: `/jobs/${j.id}` });
    }
    for (const i of items.rows) if (hit(i.number) || hit(i.name)) out.push({ label: `${i.number} · ${i.name}`, kind: 'Item', to: `/jobs/${i.job_id}?item=${i.id}` });
    for (const c of contacts.rows) if (hit(contactName(c)) || hit(c.company) || hit(c.email)) out.push({ label: contactName(c), kind: 'Contact', to: `/people?contact=${c.id}` });
    for (const m of materials.rows) if (hit(m.description) || hit(m.part_number)) out.push({ label: m.description, kind: 'Material', to: `/library/material?row=${m.id}` });
    return out.slice(0, 14);
  }, [text, opps.rows, jobs.rows, items.rows, contacts.rows, materials.rows]);

  return (
    <div className="search">
      <input type="search" placeholder="Search jobs, items, contacts, materials" value={text} onFocus={() => setArmed(true)} onChange={(e) => setText(e.target.value)} />
      {results.length > 0 && (
        <div className="results">
          {results.map((r, i) => (
            // a plain link: a Cmd-click or a middle click opens the result in a new tab and leaves the search as it is (design review D8)
            <Link key={i} to={r.to} onClick={(e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) && setText('')}>
              <span>{r.label}</span>
              <small>{r.kind}</small>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackBar() {
  const { user, device } = useSession();
  const loc = useLocation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const jobId = /^\/jobs\/([0-9a-f-]{36})/.exec(loc.pathname)?.[1] ?? null;
      await insertRow('change_log', { text: t, screen: loc.pathname + loc.search, job_id: jobId, device, by_user_id: user?.id ?? null });
      setText('');
      toast('Logged. It is in the change log.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fbar noprint"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <input type="text" placeholder="Change or bug · type or dictate it here" value={text} onChange={(e) => setText(e.target.value)} aria-label="Change or bug" />
      <Btn primary type="submit" disabled={busy || !text.trim()}>
        Log it
      </Btn>
    </form>
  );
}

export function Frame({ children }: { children: ReactNode }) {
  const s = useSession();
  const dests = useDestinations();
  const [crumb, setCrumb] = useState<Crumb[]>([]);
  const [adding, setAdding] = useState(false);
  const bottom = s.frame === 'bottom_bar';
  const showFeedback = s.setting<boolean>('feedback_bar_visible', false) === true;
  // the pane, not the page, is what scrolls, so a new screen starts at the top (design review D2, 2026-10-14);
  // a change of tab or of the selected row changes only the search, and keeps its place
  const content = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    content.current?.scrollTo({ top: 0 });
  }, [pathname]);
  // read only: a control drawn as its value takes no key (Tab and Escape move on), and nothing is dragged
  const readOnly = !s.canWrite;
  const keep = (e: React.KeyboardEvent) => {
    if (!readOnly || e.key === 'Tab' || e.key === 'Escape') return;
    if ((e.target as HTMLElement).matches?.(WRITE_CONTROLS)) e.preventDefault();
  };
  const noDrag = (e: React.DragEvent) => {
    if (readOnly) e.preventDefault();
  };

  const phone = useMemo(() => {
    const ordered = PHONE_ORDER.map((k) => dests.find((d) => d.key === k)).filter((d): d is Dest => !!d);
    return ordered.length > 5 ? { bar: ordered.slice(0, 4), more: true } : { bar: ordered, more: false };
  }, [dests]);

  // on the desk Scan is a button in the top bar; on a tablet or phone it is in the bar itself
  const rail = dests.filter((d) => d.key !== 'settings' && (s.device !== 'desk' || d.key !== 'scan'));
  const canSettings = dests.some((d) => d.key === 'settings');

  return (
    <CrumbCtx.Provider value={setCrumb}>
      <div className={`frame ${s.device}${bottom ? ' bottom' : ''}${readOnly ? ' ro' : ''}`}>
        {!bottom && (
          <nav className="rail noprint" aria-label="Destinations">
            <Link to="/" className="logo" style={{ border: 0, padding: 0 }} aria-label="ZeroGap home">
              <Mark />
            </Link>
            {rail.map((d) => (
              <NavLink key={d.key} to={d.to} end={d.end} className={({ isActive }) => (isActive ? 'on' : '')}>
                {d.label}
              </NavLink>
            ))}
            <div className="sp" />
            {canSettings && (
              <NavLink to="/settings" className={({ isActive }) => (isActive ? 'on' : '')}>
                Settings
              </NavLink>
            )}
            <span style={{ textAlign: 'center', fontSize: 11, color: '#b9c6ca', padding: '8px 4px' }}>
              {s.user?.name ?? ''}
              {readOnly && (
                <>
                  <br />
                  <ReadOnlyTag />
                </>
              )}
            </span>
          </nav>
        )}
        <div className="main">
          {s.review && (
            <div className="banner noprint">
              <span>
                <b>Local review.</b> No sign-in; you are acting as {s.user?.name ?? 'the owner'}.
              </span>
              <label>
                See it as{' '}
                <select value={s.viewAs ?? ''} onChange={(e) => s.setViewAs(e.target.value || null)}>
                  <option value="">{s.user?.role ?? 'owner'} (you)</option>
                  {ROLES.filter((r) => r !== s.user?.role).map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <header className="topbar noprint">
            <div className="crumb grow">
              {crumb.map((c, i) => (
                <span key={i}>
                  {i > 0 && <span className="sep">›</span>}
                  {c.to ? <Link to={c.to}>{c.label}</Link> : <b>{c.label}</b>}
                </span>
              ))}
            </div>
            {s.can('screen.jobs') && <Search />}
            {s.device === 'desk' && s.can('screen.scan') && (
              <Link className="btn sm" to="/scan">
                Scan
              </Link>
            )}
            {s.can('screen.jobs') && (
              <Btn small onClick={() => setAdding(true)}>
                + New
              </Btn>
            )}
            {readOnly && (bottom || s.review) && <ReadOnlyTag />}
            <Who />
          </header>
          <main className="content" ref={content} onKeyDownCapture={keep} onBeforeInputCapture={(e) => readOnly && e.preventDefault()} onDragStartCapture={noDrag}>
            {children}
          </main>
          {showFeedback && !readOnly && <FeedbackBar />}
          {bottom && (
            <nav className="bnav noprint" aria-label="Destinations">
              {phone.bar.map((d) => (
                <NavLink key={d.key} to={d.to} end={d.end} className={({ isActive }) => (isActive ? 'on' : '')}>
                  {d.label}
                </NavLink>
              ))}
              {phone.more && (
                <NavLink to="/more" className={({ isActive }) => (isActive ? 'on' : '')}>
                  More
                </NavLink>
              )}
            </nav>
          )}
        </div>
      </div>
      {adding && <NewOpportunity onClose={() => setAdding(false)} />}
    </CrumbCtx.Provider>
  );
}

/** The mark beside the name of a login that cannot change anything. */
/**
 * The name in the top bar opens a small panel saying who is signed in; Sign out is inside it, never on the name
 * itself (Spencer, 2026-10-15: a tap on his name signed him out, and the next link ran into the sender's limit).
 */
function Who() {
  const s = useSession();
  const { open, toggle, btn, pop, at, id } = usePopover(true);
  const name = String(s.user?.name ?? 'Account');
  return (
    <>
      <button type="button" ref={btn} className="btn sm read" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle} title="Who is signed in">
        {name}
      </button>
      {open &&
        createPortal(
          <div ref={pop} id={id} role="dialog" aria-label="Who is signed in" className="pop" style={{ top: at.top, left: at.left }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <div>
              <b>{name}</b>
            </div>
            <div className="muted">{[s.user?.role, s.user?.email].filter(Boolean).join(' · ')}</div>
            {s.review ? (
              <p className="muted" style={{ marginTop: 8 }}>
                Local review: no sign-in, acting as the owner.
              </p>
            ) : (
              <p style={{ marginTop: 10 }}>
                <Btn small read onClick={() => void s.signOut()}>
                  Sign out
                </Btn>
                <br />
                <small>Signing back in takes a link emailed to you.</small>
              </p>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function ReadOnlyTag() {
  return (
    <Tag kind="off" title="Only the owner can change rows. Everything here is for looking at.">
      Read only
    </Tag>
  );
}

/** The rest of the destinations, for the phone's "More". */
export function More() {
  const dests = useDestinations();
  useCrumb([{ label: 'More' }]);
  return (
    <div className="tiles">
      {dests.map((d) => (
        <Link key={d.key} className="tile" to={d.to}>
          <h3>{d.label}</h3>
        </Link>
      ))}
    </div>
  );
}
